import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime, plural, ASSET_ICONS, isITRole } from '../utils';

export default function Reports() {
  const { user, withAuth } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('terminations');
  const [terminations, setTerminations] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [loading, setLoading] = useState(true);

  const isIT = isITRole(user?.role);
  const isHR = user?.role === 'hr';

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      await withAuth(async () => {
        const promises = [];
        if (isIT || isHR) promises.push(api.getTerminationReport().then(r => setTerminations(r.data || [])));
        if (isIT) {
          promises.push(api.getExpiringLicenses({ days: 90 }).then(r => setLicenses(r.data || [])));
          promises.push(api.getUnassignedAssets().then(r => setUnassigned(r.data || [])));
        }
        await Promise.all(promises);
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [withAuth, isIT, isHR]);

  useEffect(() => { load(); }, [load]);

  const TABS = [
    { id: 'terminations', label: `Pending Returns (${terminations.length})`, show: isIT || isHR },
    { id: 'licenses', label: `Expiring Licenses (${licenses.length})`, show: isIT },
    { id: 'unassigned', label: `Unassigned (${unassigned.length})`, show: isIT },
  ].filter(t => t.show);

  return (
    <div>
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)' }}>
        <h1 style={{ fontSize: 18, fontWeight: 600 }}>Reports</h1>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, padding: '0 28px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 14px', background: 'none', border: 'none',
            borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
            color: tab === t.id ? 'var(--text-0)' : 'var(--text-2)',
            fontSize: 14, fontWeight: tab === t.id ? 500 : 400,
            cursor: 'pointer', marginBottom: -1,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '20px 28px' }}>
        {TABS.length === 0 ? (
          <div className="empty-state">
            No reports are available for your role. Contact IT if you need access.
          </div>
        ) : loading ? <div className="empty-state"><div className="spinner" /></div> : (
          <>
            {/* Terminations */}
            {tab === 'terminations' && (
              <div className="card">
                <div className="card-header">
                  <h3>Employees with Pending Device Returns</h3>
                  <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
                    {isHR ? 'Review before offboarding' : 'Assets flagged pending return'}
                  </span>
                </div>
                {terminations.length === 0
                  ? <div className="empty-state">No pending returns — all clear</div>
                  : (
                    <table>
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Location</th>
                          <th>Last Day</th>
                          <th>Flagged</th>
                          <th>HR Notified</th>
                          <th>Outstanding</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* The `|| ''` this replaces sent the user to /employees/,
                            i.e. back to the list, whenever the id was absent —
                            which was always, until the terminations query
                            started selecting it. */}
                        {terminations.map(t => (
                          <tr
                            key={t.flag_id}
                            style={{ cursor: t.employee_id ? 'pointer' : 'default' }}
                            onClick={() => t.employee_id && navigate(`/employees/${t.employee_id}`)}
                          >
                            <td>
                              <div style={{ fontWeight: 500 }}>{t.employee_name}</div>
                              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{t.job_title}</div>
                            </td>
                            <td style={{ fontSize: 14, color: 'var(--text-2)' }}>{t.home_location || '—'}</td>
                            <td style={{ fontSize: 14 }}>{formatDate(t.last_day_of_work) || '—'}</td>
                            <td style={{ fontSize: 13, color: 'var(--text-2)' }}>{formatDateTime(t.detected_at)}</td>
                            <td>
                              {t.hr_notified_at
                                ? <span className="badge badge-green">Yes</span>
                                : <span className="badge badge-yellow">Pending</span>
                              }
                            </td>
                            <td>
                              <span className={`badge ${parseInt(t.assets_outstanding) > 0 ? 'badge-red' : 'badge-green'}`}>
                                {t.assets_outstanding} {plural(t.assets_outstanding, 'device')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>
            )}

            {/* Expiring Licenses */}
            {tab === 'licenses' && isIT && (
              <div className="card">
                <div className="card-header">
                  <h3>Software Licenses Expiring Within 90 Days</h3>
                </div>
                {licenses.length === 0
                  ? <div className="empty-state">No licenses expiring soon</div>
                  : (
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Vendor</th>
                          <th>Bound Device</th>
                          <th>Location</th>
                          <th>Expires</th>
                          <th>Days Left</th>
                          <th>Renewal Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {licenses.map(l => (
                          <tr key={l.asset_tag}>
                            <td style={{ fontWeight: 500 }}>{l.product_name}</td>
                            <td style={{ color: 'var(--text-2)', fontSize: 14 }}>{l.vendor || '—'}</td>
                            <td style={{ fontSize: 14 }}>{l.bound_device_name || '—'}</td>
                            <td style={{ fontSize: 14, color: 'var(--text-2)' }}>{l.location || '—'}</td>
                            <td style={{ fontSize: 14 }}>{formatDate(l.expiry_date)}</td>
                            <td>
                              <span className={`badge ${l.days_remaining <= 14 ? 'badge-red' : l.days_remaining <= 30 ? 'badge-orange' : 'badge-yellow'}`}>
                                {l.days_remaining}d
                              </span>
                            </td>
                            <td style={{ fontSize: 14 }}>{l.renewal_cost ? `$${parseFloat(l.renewal_cost).toLocaleString()}` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>
            )}

            {/* Unassigned */}
            {tab === 'unassigned' && isIT && (
              <div className="card">
                <div className="card-header">
                  <h3>Unassigned Assets in Storage</h3>
                  <span className="badge badge-gray">{unassigned.length} devices</span>
                </div>
                {unassigned.length === 0
                  ? <div className="empty-state">No unassigned assets</div>
                  : (
                    <table>
                      <thead>
                        <tr>
                          <th>Device</th>
                          <th>Tag</th>
                          <th>Status</th>
                          <th>Storage Location</th>
                          <th>Acquired</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Route by a.id, not a.asset_tag: GET /assets/:id matches
                            a uuid column, so a tag makes Postgres reject the
                            value and the detail page shows "Asset not found".
                            Guarded because this report comes from
                            vw_unassigned_assets — if that view ever drops id,
                            a dead row beats navigating to /assets/undefined. */}
                        {unassigned.map(a => (
                          <tr
                            key={a.asset_tag}
                            style={{ cursor: a.id ? 'pointer' : 'default' }}
                            onClick={() => a.id && navigate(`/assets/${a.id}`)}
                          >
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 16 }}>{ASSET_ICONS[a.asset_type]}</span>
                                <div>
                                  <div style={{ fontWeight: 500, fontSize: 14 }}>{a.display_name}</div>
                                  <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{a.make} {a.model}</div>
                                </div>
                              </div>
                            </td>
                            <td><span className="mono">{a.asset_tag}</span></td>
                            <td><span className="badge badge-gray">{a.status?.replace(/_/g, ' ')}</span></td>
                            <td style={{ fontSize: 14, color: 'var(--text-2)' }}>{a.storage_location || '—'}</td>
                            <td style={{ fontSize: 13, color: 'var(--text-2)' }}>{formatDate(a.acquired_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}
